import { HttpBackend, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

// Azure API Management rejects a call with no subscription key (401 "Access denied due to missing
// subscription key"). The key identifies this app (its APIM subscription) — it is NOT a secret: the browser
// has to hold it, like the MSAL client id. The user is still authorised by the External ID bearer token.
//
// The keys are not built in. At startup the app asks the gateway's own keyless config endpoint
// (environment.apimConfigUrl — an APIM API answered by policy, keys held in APIM named values) which key goes
// with which gateway path. One environment can therefore hand different apps different keys, and a key can
// be rotated in APIM without rebuilding the app.
//
// A key goes ONLY to its gateway. Sent anywhere else it would leak to third parties and add a custom header
// that forces a CORS preflight the host may reject.
export const APIM_SUBSCRIPTION_HEADER = 'Ocp-Apim-Subscription-Key';

/** What the config endpoint returns: which key to send to which gateway path. */
export interface ApimGateway {
  baseUrl: string;
  subscriptionKey: string;
}

const CONFIG_TIMEOUT_MS = 5_000;

let gateways: ApimGateway[] = [];
let loading: Promise<boolean> | null = null;

/**
 * Fetches the gateway keys. Resolves true when they changed. Never rejects: without keys the app still
 * starts, and its API calls fail with APIM's 401 (logged by the auth interceptor) instead of a blank page.
 * Concurrent callers share one request.
 */
export function loadApimConfig(): Promise<boolean> {
  const url = (environment as { apimConfigUrl?: string }).apimConfigUrl;
  if (!url) return Promise.resolve(false);

  loading ??= (async () => {
    try {
      // fetch, not HttpClient: this must not go through the interceptors that depend on it.
      const res = await fetch(url, { cache: 'no-store', signal: AbortSignal.timeout(CONFIG_TIMEOUT_MS) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as { gateways?: Partial<ApimGateway>[] };
      const next = (body.gateways ?? [])
        .filter((g): g is ApimGateway => typeof g.baseUrl === 'string' && !!g.baseUrl && typeof g.subscriptionKey === 'string' && !!g.subscriptionKey)
        .map(g => ({ baseUrl: g.baseUrl.replace(/\/+$/, ''), subscriptionKey: g.subscriptionKey }));
      const changed = JSON.stringify(next) !== JSON.stringify(gateways);
      gateways = next;
      return changed;
    } catch (err) {
      console.error('Could not load the API Management keys from environment.apimConfigUrl.', err);
      return false;
    } finally {
      loading = null;
    }
  })();
  return loading;
}

/** The subscription key for `url` — that of the longest matching gateway path — or null if it is not one. */
export function apimKeyFor(url: string): string | null {
  let best: ApimGateway | null = null;
  for (const g of gateways) {
    if ((url === g.baseUrl || url.startsWith(g.baseUrl + '/')) && (!best || g.baseUrl.length > best.baseUrl.length)) {
      best = g;
    }
  }
  return best?.subscriptionKey ?? null;
}

/** APIM's own 401 for a missing or wrong key — a configuration fault, not an expired sign-in. */
export function isApimKeyRejection(body: unknown): boolean {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.includes('subscription key');
}

function addKey(req: HttpRequest<unknown>): HttpRequest<unknown> {
  const key = apimKeyFor(req.url);
  if (!key) return req;
  return req.clone({ setHeaders: { [APIM_SUBSCRIPTION_HEADER]: key } });
}

/**
 * Sends with the gateway key. If APIM refuses the key — rotated since startup, say — the keys are fetched
 * again and the request is retried once, but only if they actually changed.
 */
function sendWithKey<T>(req: HttpRequest<unknown>, send: (r: HttpRequest<unknown>) => Observable<T>): Observable<T> {
  return send(addKey(req)).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401 || !isApimKeyRejection(err.error)) {
        return throwError(() => err);
      }
      return from(loadApimConfig()).pipe(
        switchMap(changed => (changed ? send(addKey(req)) : throwError(() => err)))
      );
    })
  );
}

export const apimSubscriptionKeyInterceptor: HttpInterceptorFn = (req, next) => sendWithKey(req, r => next(r));

/**
 * Backs `NoInterceptorHttpClient`. Those anonymous calls (the public form lookup) deliberately skip the
 * auth interceptors, but through APIM they still need the key.
 */
export class ApimKeyBackend implements HttpHandler {
  constructor(private readonly backend: HttpBackend) {}

  handle(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    return sendWithKey(req, r => this.backend.handle(r));
  }
}
